import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { baseURL } from '../api/config';
import styles from './BetaSpread.module.css';

interface BetaSpreadConfig {
  pairName: string;
  symbol1: string;
  symbol2: string;
  enabled: boolean;
  windowSize: number;
  maxTickAgeSec: number;
  maxTickDeltaSec: number;
  minTicksPerMinute: number;
  maxZScore: number;
  marketHoursSymbol1?: string;
  marketHoursSymbol2?: string;
  createdAt: string;
  updatedAt: string;
}

interface SpreadDataPoint {
  time: string;
  spread: number;
  beta: number;
  correlation: number;
  zScore: number | null;  // Z-Score for trading signals
  isValid: boolean;
}

interface MultiTimeframeData {
  corrH1: number | null;
  corrH4: number | null;
  corrD1: number | null;
  betaH1: number | null;
  betaH4: number | null;
  betaD1: number | null;
  avgCorr: number | null;
  score: string | null;
  scoreColor: string | null;
}


interface LeadLagAnalysis {
  time: string;
  pairName: string;
  d1Leader: string | null;
  d1Confidence: number;
  d1LagBars: number;
  d1Correlation: number;
  h4Leader: string | null;
  h4Confidence: number;
  h4LagBars: number;
  h4Correlation: number;
  h1Leader: string | null;
  h1Confidence: number;
  h1LagBars: number;
  h1Correlation: number;
  m15Leader: string | null;
  m15Confidence: number;
  m15LagBars: number;
  m15Correlation: number;
  combinedLeader: string | null;
  combinedConfidence: number;
  regime: string;
  timeframesAgree: number;
  dataQualityScore: number;
}

interface PercentileBandMetrics {
  time: string;
  pairName: string;
  p1: number;
  p5: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  p99: number;
  dailyHigh: number;
  dailyLow: number;
  dailyRange: number;
  yesterdayHigh: number | null;
  yesterdayLow: number | null;
  yesterdayRange: number | null;
  meanSpread: number;
  stdDevSpread: number;
  windowSize: number;
  session: string;
  sessionMultiplier: number;
  adjustedUpper: number;
  adjustedLower: number;
  volatilityRegime: string;
  regimeScore: number;
  calculationTimeMs?: number;
}

const BetaSpread = () => {
  // State management
  const [configs, setConfigs] = useState<BetaSpreadConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPair, setSelectedPair] = useState<string | null>(null);
  const [spreadData, setSpreadData] = useState<SpreadDataPoint[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [, setTick] = useState(0); // Force re-render for timestamp updates
  const [multiTfData, setMultiTfData] = useState<MultiTimeframeData | null>(null);
  const [leadLagData, setLeadLagData] = useState<LeadLagAnalysis | null>(null);
  const [leadLagLoading, setLeadLagLoading] = useState(false);
  const [leadLagError, setLeadLagError] = useState<string | null>(null);
  const [showLeadLag, setShowLeadLag] = useState(false);
  const [percentileData, setPercentileData] = useState<PercentileBandMetrics | null>(null);
  const [percentileLoading, setPercentileLoading] = useState(false);
  const [percentileError, setPercentileError] = useState<string | null>(null);
  const [showPercentile, setShowPercentile] = useState(false);

  // Default display values when no data available
  const defaultLeadLag: LeadLagAnalysis = {
    time: new Date().toISOString(),
    pairName: selectedPair || 'N/A',
    d1Leader: null,
    d1Confidence: 0,
    d1LagBars: 0,
    d1Correlation: 0,
    h4Leader: null,
    h4Confidence: 0,
    h4LagBars: 0,
    h4Correlation: 0,
    h1Leader: null,
    h1Confidence: 0,
    h1LagBars: 0,
    h1Correlation: 0,
    m15Leader: null,
    m15Confidence: 0,
    m15LagBars: 0,
    m15Correlation: 0,
    combinedLeader: null,
    combinedConfidence: 0,
    timeframesAgree: 0,
    regime: 'NO_DATA',
    dataQualityScore: 0
  };

  const defaultPercentile: PercentileBandMetrics = {
    time: new Date().toISOString(),
    pairName: selectedPair || 'N/A',
    p1: 0,
    p5: 0,
    p25: 0,
    p50: 0,
    p75: 0,
    p95: 0,
    p99: 0,
    dailyHigh: 0,
    dailyLow: 0,
    dailyRange: 0,
    yesterdayHigh: null,
    yesterdayLow: null,
    yesterdayRange: null,
    meanSpread: 0,
    stdDevSpread: 0,
    windowSize: 0,
    session: 'N/A',
    sessionMultiplier: 1,
    adjustedUpper: 0,
    adjustedLower: 0,
    volatilityRegime: 'NO_DATA',
    regimeScore: 0
  };

  // Use actual data or defaults
  const displayLeadLag = leadLagData || defaultLeadLag;
  const displayPercentile = percentileData || defaultPercentile;

  // Form state
  const [formData, setFormData] = useState<Partial<BetaSpreadConfig>>({
    pairName: '',
    symbol1: '',
    symbol2: '',
    enabled: true,
    windowSize: 60,
    maxTickAgeSec: 10,
    maxTickDeltaSec: 5,
    minTicksPerMinute: 5,
    maxZScore: 3.0
  });

  useEffect(() => {
    loadConfigs();
    loadAvailableSymbols();
  }, []);

  useEffect(() => {
    // spreadData updated
  }, [spreadData]);

  // Update timestamp display every second
  useEffect(() => {
    if (!lastUpdate) return;

    const intervalId = setInterval(() => {
      // Force re-render to update "Xs ago" display
      setTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [lastUpdate]);

  const loadConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/api/betaspread/configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(res.data || []);
      setLoading(false);
    } catch (err: any) {
      console.error('[BetaSpread] Failed to load BetaSpread configs:', err);
      console.error('[BetaSpread] Error response:', err.response?.data);
      setLoading(false);
    }
  };

  const loadAvailableSymbols = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/api/symbols/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for active symbols and use unique symbol name
      const symbols = (res.data || [])
        .filter((s: any) => s.isActive && s.name)
        .map((s: any) => s.name);
      setAvailableSymbols(symbols);
    } catch (err) {
      console.error('Failed to load symbols:', err);
      // Fallback to common symbols
      setAvailableSymbols(['XAUUSD_ct', 'XAUEUR_ct', 'EURUSD_ct', 'XAUUSD_ct2', 'EURUSD_ct2']);
    }
  };

  const loadSpreadData = async (pairName: string) => {
    setIsRefreshing(true);

    try {
      const token = localStorage.getItem('token');
      const encodedPairName = encodeURIComponent(pairName);
      const url = `${baseURL}/api/betaspread/data/${encodedPairName}`;

      const res = await axios.get(url, {
        params: { limit: 100 },
        headers: { Authorization: `Bearer ${token}` }
      });

      setSpreadData(res.data || []);
      setLastUpdate(new Date());

      // Load multi-timeframe data
      loadMultiTimeframeData(pairName);
      loadLeadLagData(pairName);
      loadPercentileData(pairName);
    } catch (err: any) {
      console.error('[BetaSpread] Failed to load spread data:', err);
      console.error('[BetaSpread] Error response:', err.response?.data);
      console.error('[BetaSpread] Error status:', err.response?.status);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadMultiTimeframeData = async (pairName: string) => {
    try {
      const token = localStorage.getItem('token');
      const encodedPairName = encodeURIComponent(pairName);
      const url = `${baseURL}/api/betaspread/multi-timeframe/${encodedPairName}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMultiTfData(res.data);
    } catch (err: any) {
      console.error('[BetaSpread] Failed to load multi-timeframe data:', err);
      console.error('[BetaSpread] Error response:', err.response?.data);
      console.error('[BetaSpread] Error status:', err.response?.status);
      // Don't show error to user - multi-TF data is optional
    }
  };

  const loadLeadLagData = async (pairName: string) => {
    setLeadLagLoading(true);
    setLeadLagError(null);
    try {
      const token = localStorage.getItem('token');
      const encodedPairName = encodeURIComponent(pairName);
      const url = `${baseURL}/api/leadlag/${encodedPairName}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data) {
        console.error('[DEBUG] Lead-Lag API SUCCESS:', {
          url,
          hasData: !!res.data,
          data: res.data
        });
        setLeadLagData(res.data);
        setLeadLagError(null);
      } else {
        console.error('[DEBUG] Lead-Lag API returned empty data:', { url });
        setLeadLagData(null);
        setLeadLagError('No data available');
      }
    } catch (err: any) {
      console.error('[DEBUG] Lead-Lag API FAILED:', {
        url: `${baseURL}/api/leadlag/${encodeURIComponent(pairName)}`,
        status: err.response?.status,
        statusText: err.response?.statusText,
        error: err.response?.data || err.message
      });
      setLeadLagData(null);
      if (err.response?.status === 404) {
        setLeadLagError('Insufficient data - waiting for bars to accumulate (needs 100+ M15 bars)');
      } else {
        setLeadLagError(`Error: ${err.response?.statusText || err.message}`);
      }
    } finally {
      setLeadLagLoading(false);
    }
  };

  const loadPercentileData = async (pairName: string) => {
    setPercentileLoading(true);
    setPercentileError(null);
    try {
      const token = localStorage.getItem('token');
      const encodedPairName = encodeURIComponent(pairName);
      const url = `${baseURL}/api/percentile/${encodedPairName}/latest`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data) {
        console.error('[DEBUG] Percentile API SUCCESS:', {
          url,
          hasData: !!res.data,
          data: res.data
        });
        setPercentileData(res.data);
        setPercentileError(null);
      } else {
        console.error('[DEBUG] Percentile API returned empty data:', { url });
        setPercentileData(null);
        setPercentileError('No data available');
      }
    } catch (err: any) {
      console.error('[DEBUG] Percentile API FAILED:', {
        url: `${baseURL}/api/percentile/${encodeURIComponent(pairName)}/latest`,
        status: err.response?.status,
        statusText: err.response?.statusText,
        error: err.response?.data || err.message
      });
      setPercentileData(null);
      if (err.response?.status === 404) {
        setPercentileError('Service not running - percentile bands require manual calculation');
      } else {
        setPercentileError(`Error: ${err.response?.statusText || err.message}`);
      }
    } finally {
      setPercentileLoading(false);
    }
  };

  const handleCreatePair = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${baseURL}/api/betaspread/configs`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Hot-reload BetaSpread calculator configuration without server restart
      try {
        await axios.post(`${baseURL}/api/betaspread/reload`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('BetaSpread configuration reloaded successfully');
      } catch (reloadErr: any) {
        console.warn('Config created but reload failed:', reloadErr.message);
        // Don't fail the operation if reload fails - config is saved in DB
      }

      await loadConfigs();
      setShowCreateForm(false);
      // Reset form
      setFormData({
        pairName: '',
        symbol1: '',
        symbol2: '',
        enabled: true,
        windowSize: 60,
        maxTickAgeSec: 10,
        maxTickDeltaSec: 5,
        minTicksPerMinute: 5,
        maxZScore: 3.0
      });
    } catch (err: any) {
      console.error('Failed to create pair:', err);
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeletePair = async (pairName: string) => {
    if (!window.confirm(`Delete pair ${pairName}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/api/betaspread/configs/${pairName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadConfigs();
      setSelectedPair(null);
    } catch (err) {
      console.error('Failed to delete pair:', err);
    }
  };

  const handleToggleEnabled = async (config: BetaSpreadConfig) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${baseURL}/api/betaspread/configs`,
        { ...config, enabled: !config.enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadConfigs();
    } catch (err) {
      console.error('Failed to toggle pair:', err);
    }
  };

  const getRegimeClass = (regime: string, type: 'bg' | 'border') => {
    const prefix = type === 'bg' ? 'bg' : 'border';
    switch (regime) {
      case 'TRENDING': return styles[`${prefix}HighConsensus`];
      case 'MEAN_REVERTING': return styles[`${prefix}ModerateConsensus`];
      default: return styles[`${prefix}NoData`];
    }
  };

  const getVolatilityClass = (regime: string, type: 'bg' | 'border') => {
    const prefix = type === 'bg' ? 'bg' : 'border';
    switch (regime) {
      case 'LOW': return styles[`${prefix}LowVolatility`];
      case 'NORMAL': return styles[`${prefix}NormalVolatility`];
      case 'HIGH': return styles[`${prefix}HighVolatility`];
      case 'EXTREME': return styles[`${prefix}ExtremeVolatility`];
      default: return styles[`${prefix}NoData`];
    }
  };

  const getSessionClass = (session: string) => {
    switch (session) {
      case 'ASIAN': return styles.bgAsianSession;
      case 'EUROPEAN': return styles.bgEuropeanSession;
      case 'AMERICAN': return styles.bgAmericanSession;
      case 'OVERLAP': return styles.bgOverlapSession;
      default: return styles.bgOffHoursSession;
    }
  };

  const getZScoreClasses = (zScore: number) => {
    const absZ = Math.abs(zScore);
    if (absZ > 2.0) {
      return {
        border: styles.zScoreBorderStrong,
        value: styles.zScoreValueStrong,
        signal: styles.zScoreSignalStrong
      };
    } else if (absZ > 1.0) {
      return {
        border: styles.zScoreBorderWeak,
        value: styles.zScoreValueWeak,
        signal: styles.zScoreSignalWeak
      };
    } else {
      return {
        border: styles.zScoreBorderNeutral,
        value: styles.zScoreValueNeutral,
        signal: styles.zScoreSignalNeutral
      };
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className={styles.loadingContainer}>Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.pageContainer}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            BetaSpread Configuration
          </h1>
          <p className={styles.pageDescription}>
            Manage spread trading pairs, correlation monitoring, and threshold optimization
          </p>
        </div>

        {/* Main Content Grid */}
        <div className={styles.mainGrid}>
          {/* LEFT: Pair List */}
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h2 className={styles.sidebarTitle}>
                Configured Pairs ({configs.length})
              </h2>
              <button
                onClick={() => setShowCreateForm(true)}
                className={styles.newPairButton}
              >
                + New Pair
              </button>
            </div>

            {/* Pair Cards */}
            {configs.map(config => (
              <div
                key={config.pairName}
                onClick={() => {
                  setSelectedPair(config.pairName);
                  loadSpreadData(config.pairName);
                }}
                className={`${styles.pairCard} ${selectedPair === config.pairName ? styles.pairCardSelected : ''}`}
              >
                <div className={styles.pairCardContent}>
                  <div className={styles.pairInfo}>
                    <div className={styles.pairName}>
                      {config.pairName}
                    </div>
                    <div className={styles.pairSymbols}>
                      {config.symbol1} / {config.symbol2}
                    </div>
                  </div>
                  <div className={`${styles.statusIndicator} ${config.enabled ? styles.statusEnabled : styles.statusDisabled}`} />
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT: Details & Configuration */}
          <div>
            {selectedPair ? (
              <>
                {/* Live Monitor */}
                <div className={styles.liveMonitor}>
                  {/* Header with Refresh Button */}
                  <div className={styles.monitorHeader}>
                    <h2 className={styles.monitorTitle}>
                      Live Monitor: {selectedPair}
                    </h2>
                    <button
                      onClick={() => selectedPair && loadSpreadData(selectedPair)}
                      disabled={isRefreshing}
                      className={styles.refreshButton}
                    >
                      <span className={`${styles.refreshIcon} ${isRefreshing ? styles.refreshIconSpinning : ''}`}>
                        🔄
                      </span>
                      {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                  </div>

                  <div className={styles.statsGrid}>
                    {/* Current Spread */}
                    <div className={styles.statCard}>
                      <div className={styles.statLabel}>
                        Current Spread
                      </div>
                      <div className={`${styles.statValue} ${styles.statValueBlue}`}>
                        {spreadData[0]?.spread?.toFixed(5) || '--'}
                      </div>
                    </div>

                    {/* Beta */}
                    <div className={styles.statCard}>
                      <div className={styles.statLabel}>
                        Beta
                      </div>
                      <div className={`${styles.statValue} ${styles.statValueGreen}`}>
                        {spreadData[0]?.beta?.toFixed(4) || '--'}
                      </div>
                    </div>

                    {/* Correlation */}
                    <div className={styles.statCard}>
                      <div className={styles.statLabel}>
                        Correlation (H1)
                      </div>
                      <div className={`${styles.statValue} ${styles.statValueOrange}`}>
                        {spreadData[0]?.correlation?.toFixed(4) || '--'}
                      </div>

                      {/* Multi-Timeframe Correlation */}
                      {multiTfData && multiTfData.avgCorr && (
                        <div className={styles.multiTfDetails}>
                          <div className={styles.multiTfRow}>
                            Avg: <span className={styles.multiTfAvg}>
                              {multiTfData.avgCorr.toFixed(4)}
                            </span>
                            {multiTfData.score && (
                              <span
                                className={styles.multiTfBadge}
                                style={{ backgroundColor: multiTfData.scoreColor || '#64748B' }}
                              >
                                {multiTfData.score}
                              </span>
                            )}
                          </div>
                          <div className={styles.multiTfTimeframes}>
                            <span>H1: {multiTfData.corrH1?.toFixed(3) || '--'}</span>
                            <span>H4: {multiTfData.corrH4?.toFixed(3) || '--'}</span>
                            <span>D1: {multiTfData.corrD1?.toFixed(3) || '--'}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className={styles.statCard}>
                      <div className={styles.statLabel}>
                        Status
                      </div>
                      <div className={`${styles.statValue} ${spreadData[0]?.isValid ? styles.statValueGreen : styles.statValueRed}`}>
                        {spreadData[0]?.isValid ? 'VALID' : 'INVALID'}
                      </div>
                    </div>
                  </div>

                  {/* Z-Score Trading Signal */}
                  {spreadData[0]?.zScore !== null && spreadData[0]?.zScore !== undefined && (() => {
                    const zClasses = getZScoreClasses(spreadData[0].zScore);
                    const absZ = Math.abs(spreadData[0].zScore);
                    return (
                      <div className={`${styles.zScoreContainer} ${zClasses.border}`}>
                        <div className={styles.zScoreContent}>
                          <div className={styles.zScoreLabel}>
                            Z-Score Trading Signal
                          </div>
                          <div className={`${styles.zScoreValue} ${zClasses.value}`}>
                            {spreadData[0].zScore.toFixed(3)}
                          </div>
                          <div className={`${styles.zScoreSignal} ${zClasses.signal}`}>
                            {absZ > 2.0
                              ? (spreadData[0].zScore > 0 ? '🔴 STRONG BUY_SPREAD' : '🟢 STRONG SELL_SPREAD')
                              : absZ > 1.0
                              ? (spreadData[0].zScore > 0 ? '🟠 WEAK BUY_SPREAD' : '🟡 WEAK SELL_SPREAD')
                              : '⚪ NO_TRADE'
                            }
                          </div>
                          <div className={styles.zScoreHint}>
                            {spreadData[0].zScore > 0
                              ? 'Spread above mean → Short Sym1, Long Sym2'
                              : 'Spread below mean → Long Sym1, Short Sym2'
                            }
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Lead-Lag Analysis Panel - ALWAYS SHOW */}
                  <div className={`${styles.panelContainer} ${getRegimeClass(displayLeadLag.regime, 'border')}`}>
                    <div
                      onClick={() => setShowLeadLag(!showLeadLag)}
                      className={`${styles.panelHeader} ${showLeadLag ? styles.expanded : ''}`}
                    >
                      <div className={styles.panelTitle}>📊 LEAD-LAG ANALYSIS</div>
                      <div className={styles.panelControls}>
                        {leadLagLoading && <span className={styles.loadingText}>Loading...</span>}
                        {!leadLagLoading && leadLagError && !leadLagData && (
                          <span className={styles.errorText}>{leadLagError}</span>
                        )}
                        <div className={`${styles.badge} ${getRegimeClass(displayLeadLag.regime, 'bg')}`}>
                          {displayLeadLag.regime}
                        </div>
                        <span className={styles.toggleIcon}>{showLeadLag ? '▼' : '▶'}</span>
                      </div>
                    </div>

                    {/* Details section - ALWAYS show when expanded */}
                    {showLeadLag && (
                      <div>
                        <div className={`${styles.contentSection} ${getRegimeClass(displayLeadLag.regime, 'border')}`}>
                          <div className={styles.gridTwo}>
                            <div>
                              <span className={styles.label}>Leader: </span>
                              <span className={styles.value}>{displayLeadLag.combinedLeader || 'N/A'}</span>
                            </div>
                            <div>
                              <span className={styles.label}>Confidence: </span>
                              <span className={styles.value}>{(displayLeadLag.combinedConfidence * 100).toFixed(0)}%</span>
                            </div>
                            <div>
                              <span className={styles.label}>Agreement: </span>
                              <span className={styles.value}>{displayLeadLag.timeframesAgree}/4</span>
                            </div>
                            <div>
                              <span className={styles.label}>Quality: </span>
                              <span className={styles.value}>{(displayLeadLag.dataQualityScore * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>

                        <div className={styles.gridTwoSmall}>
                          <div className={styles.timeframeCard}>
                            <div className={styles.timeframeLabel}>D1 (Daily)</div>
                            <div className={styles.timeframeValue}>
                              {displayLeadLag.d1Leader || 'N/A'}
                              {displayLeadLag.d1LagBars !== 0 && ` (${Math.abs(displayLeadLag.d1LagBars)} bars)`}
                            </div>
                            <div className={styles.timeframeConfidence}>Conf: {(displayLeadLag.d1Confidence * 100).toFixed(0)}%</div>
                          </div>
                          <div className={styles.timeframeCard}>
                            <div className={styles.timeframeLabel}>H4 (4-Hour)</div>
                            <div className={styles.timeframeValue}>
                              {displayLeadLag.h4Leader || 'N/A'}
                              {displayLeadLag.h4LagBars !== 0 && ` (${Math.abs(displayLeadLag.h4LagBars)} bars)`}
                            </div>
                            <div className={styles.timeframeConfidence}>Conf: {(displayLeadLag.h4Confidence * 100).toFixed(0)}%</div>
                          </div>
                          <div className={styles.timeframeCard}>
                            <div className={styles.timeframeLabel}>H1 (1-Hour)</div>
                            <div className={styles.timeframeValue}>
                              {displayLeadLag.h1Leader || 'N/A'}
                              {displayLeadLag.h1LagBars !== 0 && ` (${Math.abs(displayLeadLag.h1LagBars)} bars)`}
                            </div>
                            <div className={styles.timeframeConfidence}>Conf: {(displayLeadLag.h1Confidence * 100).toFixed(0)}%</div>
                          </div>
                          <div className={styles.timeframeCard}>
                            <div className={styles.timeframeLabel}>M15 (15-Min)</div>
                            <div className={styles.timeframeValue}>
                              {displayLeadLag.m15Leader || 'N/A'}
                              {displayLeadLag.m15LagBars !== 0 && ` (${Math.abs(displayLeadLag.m15LagBars)} bars)`}
                            </div>
                            <div className={styles.timeframeConfidence}>Conf: {(displayLeadLag.m15Confidence * 100).toFixed(0)}%</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Percentile Bands Panel - ALWAYS SHOW */}
                  <div className={`${styles.panelContainer} ${getVolatilityClass(displayPercentile.volatilityRegime, 'border')}`}>
                    <div
                      onClick={() => setShowPercentile(!showPercentile)}
                      className={`${styles.panelHeader} ${showPercentile ? styles.expanded : ''}`}
                    >
                      <div className={styles.panelTitle}>📈 PERCENTILE BANDS</div>
                      <div className={styles.panelControls}>
                        {percentileLoading && <span className={styles.loadingText}>Loading...</span>}
                        {!percentileLoading && percentileError && !percentileData && (
                          <span className={styles.errorText}>{percentileError}</span>
                        )}
                        <div className={`${styles.badgeSmall} ${getSessionClass(displayPercentile.session)}`}>
                          {displayPercentile.session} {displayPercentile.sessionMultiplier}x
                        </div>
                        <div className={`${styles.badgeSmall} ${getVolatilityClass(displayPercentile.volatilityRegime, 'bg')}`}>
                          {displayPercentile.volatilityRegime}
                        </div>
                        <span className={styles.dailyRange}>
                          ↑{displayPercentile.dailyHigh.toFixed(2)} ↓{displayPercentile.dailyLow.toFixed(2)}
                        </span>
                        <span className={styles.toggleIcon}>{showPercentile ? '▼' : '▶'}</span>
                      </div>
                    </div>

                    {/* Details section - ALWAYS show when expanded */}
                    {showPercentile && (
                        <div>
                          {/* TODAY'S RANGE */}
                          <div className={styles.contentSection}>
                            <div className={styles.sectionTitle}>TODAY'S RANGE</div>
                            <div className={styles.gridThree}>
                              <div>
                                <span className={styles.label}>High: </span>
                                <span className={styles.valueSuccess}>{displayPercentile.dailyHigh.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className={styles.label}>Low: </span>
                                <span className={styles.valueError}>{displayPercentile.dailyLow.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className={styles.label}>Range: </span>
                                <span className={styles.value}>{displayPercentile.dailyRange.toFixed(2)}</span>
                              </div>
                            </div>
                            <div className={styles.gridTwoSmallMargin}>
                              <div>
                                <span className={styles.label}>Hit Upper ({displayPercentile.adjustedUpper.toFixed(2)})? </span>
                                <span style={{ color: displayPercentile.dailyHigh >= displayPercentile.adjustedUpper ? '#10B981' : '#64748B' }}>
                                  {displayPercentile.dailyHigh >= displayPercentile.adjustedUpper ? '✓' : '✗'}
                                </span>
                              </div>
                              <div>
                                <span className={styles.label}>Hit Lower ({displayPercentile.adjustedLower.toFixed(2)})? </span>
                                <span style={{ color: displayPercentile.dailyLow <= displayPercentile.adjustedLower ? '#10B981' : '#64748B' }}>
                                  {displayPercentile.dailyLow <= displayPercentile.adjustedLower ? '✓' : '✗'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* YESTERDAY'S RANGE */}
                          <div className={styles.contentSection}>
                            <div className={styles.sectionTitle}>YESTERDAY'S RANGE</div>
                            <div className={styles.gridThree}>
                              <div>
                                <span className={styles.label}>High: </span>
                                <span className={styles.valueSuccess}>
                                  {displayPercentile.yesterdayHigh !== null ? displayPercentile.yesterdayHigh.toFixed(2) : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className={styles.label}>Low: </span>
                                <span className={styles.valueError}>
                                  {displayPercentile.yesterdayLow !== null ? displayPercentile.yesterdayLow.toFixed(2) : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className={styles.label}>Range: </span>
                                <span className={styles.value}>
                                  {displayPercentile.yesterdayRange !== null ? displayPercentile.yesterdayRange.toFixed(2) : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* ENTRY ZONES */}
                          <div className={styles.contentSectionWarning}>
                            <div className={styles.sectionTitle}>ENTRY ZONES - Session Adjusted</div>
                            <div className={styles.gridTwoSmall}>
                              <div>
                                <span className={styles.label}>Upper: </span>
                                <span className={styles.valueWarning}>
                                  {displayPercentile.adjustedUpper.toFixed(2)} (P95×{displayPercentile.sessionMultiplier})
                                </span>
                              </div>
                              <div>
                                <span className={styles.label}>Lower: </span>
                                <span className={styles.valueWarning}>
                                  {displayPercentile.adjustedLower.toFixed(2)} (P5×{displayPercentile.sessionMultiplier})
                                </span>
                              </div>
                              <div>
                                <span className={styles.label}>Median: </span>
                                <span className={styles.value}>{displayPercentile.p50.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className={styles.label}>Window: </span>
                                <span className={styles.value}>{displayPercentile.windowSize} M15</span>
                              </div>
                            </div>
                          </div>

                          {/* PERCENTILE BANDS - Visual */}
                          <div className={styles.contentSection}>
                            <div className={styles.sectionTitle}>PERCENTILE BANDS</div>
                            <div className={styles.percentileBands}>
                              {[
                                { label: 'P99', value: displayPercentile.p99, color: '#EF4444' },
                                { label: 'P95', value: displayPercentile.p95, color: '#FBBF24', highlight: true },
                                { label: 'P75', value: displayPercentile.p75, color: '#94A3B8' },
                                { label: 'P50', value: displayPercentile.p50, color: '#3B82F6' },
                                { label: 'P25', value: displayPercentile.p25, color: '#94A3B8' },
                                { label: 'P5',  value: displayPercentile.p5,  color: '#FBBF24', highlight: true },
                                { label: 'P1',  value: displayPercentile.p1,  color: '#EF4444' }
                              ].map((p, idx) => {
                                const p1 = displayPercentile.p1;
                                const p99 = displayPercentile.p99 || 1;
                                const widthPercent = p99 > p1 ? Math.min(100, Math.max(0, ((p.value - p1) / (p99 - p1)) * 100)) : 0;
                                return (
                                  <div key={idx} className={`${styles.percentileRow} ${p.highlight ? styles.highlight : ''}`}>
                                    <span className={styles.percentileLabel} style={{ color: p.color }}>{p.label}</span>
                                    <div className={styles.percentileBar}>
                                      <div
                                        className={styles.percentileFill}
                                        style={{ width: `${widthPercent}%`, backgroundColor: p.color }}
                                      ></div>
                                    </div>
                                    <span className={styles.percentileValue}>
                                      {p.value.toFixed(2)}
                                      {p.highlight && <span className={styles.percentileArrow}>←</span>}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* STATISTICS */}
                          <div className={styles.contentSection}>
                            <div className={styles.sectionTitle}>STATISTICS</div>
                            <div className={styles.gridThree}>
                              <div>
                                <span className={styles.label}>Mean: </span>
                                <span className={styles.value}>{displayPercentile.meanSpread.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className={styles.label}>StdDev: </span>
                                <span className={styles.value}>{displayPercentile.stdDevSpread.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className={styles.label}>Bars: </span>
                                <span className={styles.value}>{displayPercentile.windowSize} (≈{Math.round(displayPercentile.windowSize * 15 / 60)}h)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>


                  {/* Last Update Info */}
                  <div className={styles.lastUpdateContainer}>
                    <div className={styles.lastUpdateText}>
                      <div>Last DB Record: {spreadData[0]?.time ? new Date(spreadData[0].time).toLocaleString() : 'No data'}</div>
                    </div>
                    <div className={styles.lastUpdateTextRight}>
                      <div>Last Refreshed: {lastUpdate ? (() => {
                        const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
                        if (seconds < 60) return `${seconds}s ago`;
                        const minutes = Math.floor(seconds / 60);
                        if (minutes < 60) return `${minutes}m ago`;
                        const hours = Math.floor(minutes / 60);
                        return `${hours}h ${minutes % 60}m ago`;
                      })() : 'Never'}</div>
                    </div>
                  </div>
                </div>

                {/* Pair Configuration Details */}
                <div className={styles.configContainer}>
                  <div className={styles.configHeader}>
                    <h2 className={styles.configHeaderTitle}>
                      Configuration Details
                    </h2>
                    <button
                      onClick={() => handleDeletePair(selectedPair)}
                      className={styles.deleteButton}
                    >
                      Delete Pair
                    </button>
                  </div>

                  {configs.filter(c => c.pairName === selectedPair).map(config => (
                    <div key={config.pairName} className={styles.configGrid}>
                      <div className={styles.configCard}>
                        <div className={styles.configCardLabel}>Window Size</div>
                        <div className={styles.configCardValue}>{config.windowSize} bars</div>
                      </div>
                      <div className={styles.configCard}>
                        <div className={styles.configCardLabel}>Max Tick Age</div>
                        <div className={styles.configCardValue}>{config.maxTickAgeSec}s</div>
                      </div>
                      <div className={styles.configCard}>
                        <div className={styles.configCardLabel}>Max Tick Delta</div>
                        <div className={styles.configCardValue}>{config.maxTickDeltaSec}s</div>
                      </div>
                      <div className={styles.configCard}>
                        <div className={styles.configCardLabel}>Min Ticks/Min</div>
                        <div className={styles.configCardValue}>{config.minTicksPerMinute}</div>
                      </div>
                      <div className={styles.configCard}>
                        <div className={styles.configCardLabel}>Max Z-Score</div>
                        <div className={styles.configCardValue}>{config.maxZScore}</div>
                      </div>
                      <div className={styles.configCard}>
                        <div className={styles.configCardLabel}>Status</div>
                        <div
                          className={`${styles.configCardValueClickable} ${config.enabled ? styles.configCardValueEnabled : styles.configCardValueDisabled}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleEnabled(config);
                          }}
                        >
                          {config.enabled ? 'ENABLED' : 'DISABLED'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📊</div>
                <div className={styles.emptyStateTitle}>Select a pair to view details</div>
                <div className={styles.emptyStateSubtitle}>or create a new one to get started</div>
              </div>
            )}
          </div>
        </div>

        {/* Create/Edit Pair Form Modal */}
        {showCreateForm && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h2 className={styles.modalTitle}>
                Create New Pair
              </h2>

              {/* Pair Name */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Pair Name
                </label>
                <input
                  type="text"
                  value={formData.pairName}
                  onChange={(e) => setFormData({...formData, pairName: e.target.value})}
                  placeholder="e.g., GOLD_FUTURES"
                  className={styles.formInput}
                />
              </div>

              {/* Symbol1 Dropdown */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Symbol 1
                </label>
                <select
                  value={formData.symbol1}
                  onChange={(e) => setFormData({...formData, symbol1: e.target.value})}
                  className={styles.formSelect}
                >
                  <option value="">Select symbol...</option>
                  {availableSymbols.map(sym => (
                    <option key={sym} value={sym}>{sym}</option>
                  ))}
                </select>
              </div>

              {/* Symbol2 Dropdown */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Symbol 2
                </label>
                <select
                  value={formData.symbol2}
                  onChange={(e) => setFormData({...formData, symbol2: e.target.value})}
                  className={styles.formSelect}
                >
                  <option value="">Select symbol...</option>
                  {availableSymbols.map(sym => (
                    <option key={sym} value={sym}>{sym}</option>
                  ))}
                </select>
              </div>

              {/* Window Size Slider */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Window Size: {formData.windowSize} bars
                </label>
                <input
                  type="range"
                  min="20"
                  max="200"
                  step="10"
                  value={formData.windowSize}
                  onChange={(e) => setFormData({...formData, windowSize: parseInt(e.target.value)})}
                  className={styles.formSlider}
                />
                <div className={styles.sliderLabels}>
                  <span>20</span>
                  <span>200</span>
                </div>
              </div>

              {/* Threshold Grid */}
              <div className={styles.formGridTwo}>
                {/* Max Tick Age */}
                <div>
                  <label className={styles.formLabel}>
                    Max Tick Age (sec)
                  </label>
                  <input
                    type="number"
                    value={formData.maxTickAgeSec}
                    onChange={(e) => setFormData({...formData, maxTickAgeSec: parseInt(e.target.value)})}
                    className={styles.formInput}
                  />
                </div>

                {/* Max Tick Delta */}
                <div>
                  <label className={styles.formLabel}>
                    Max Tick Delta (sec)
                  </label>
                  <input
                    type="number"
                    value={formData.maxTickDeltaSec}
                    onChange={(e) => setFormData({...formData, maxTickDeltaSec: parseInt(e.target.value)})}
                    className={styles.formInput}
                  />
                </div>

                {/* Min Ticks Per Minute */}
                <div>
                  <label className={styles.formLabel}>
                    Min Ticks/Minute
                  </label>
                  <input
                    type="number"
                    value={formData.minTicksPerMinute}
                    onChange={(e) => setFormData({...formData, minTicksPerMinute: parseInt(e.target.value)})}
                    className={styles.formInput}
                  />
                </div>

                {/* Max Z-Score */}
                <div>
                  <label className={styles.formLabel}>
                    Max Z-Score
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.maxZScore}
                    onChange={(e) => setFormData({...formData, maxZScore: parseFloat(e.target.value)})}
                    className={styles.formInput}
                  />
                </div>
              </div>

              {/* Enable Toggle */}
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Enable this pair</span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className={styles.formActions}>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePair}
                  className={styles.createButton}
                >
                  Create Pair
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BetaSpread;
